import * as Schema from "effect/Schema";
import {
  PreviewAutomationClickInput,
  PreviewAutomationEvaluateInput,
  PreviewAutomationPressInput,
  PreviewAutomationScrollInput,
  PreviewAutomationSnapshot,
  PreviewAutomationStatus,
  PreviewAutomationTypeInput,
  PreviewAutomationWaitForInput,
} from "./previewAutomation.ts";
import { EnvironmentId } from "./baseSchemas.ts";
import { AuthAccessTokenResult } from "./auth.ts";
import { AdvertisedEndpoint } from "./remoteAccess.ts";
import { ExecutionEnvironmentDescriptor } from "./environment.ts";
import type { ClientSettings, ClientSettingsPatch } from "./settings.ts";
import type { EditorId } from "./editor.ts";

export interface ContextMenuItem<T extends string = string> {
  id: T;
  label: string;
  destructive?: boolean;
  disabled?: boolean;
  /** Renders as a non-interactive section header label. Web fallback only — stripped on desktop native menus. */
  header?: boolean;
  /** Icon keyword resolved by the web fallback. Stripped on desktop native menus. */
  icon?: string;
  children?: readonly ContextMenuItem<T>[];
}

export interface ContextMenuItemSchemaType {
  readonly id: string;
  readonly label: string;
  readonly destructive?: boolean;
  readonly disabled?: boolean;
  readonly header?: boolean;
  readonly icon?: string;
  readonly children?: readonly ContextMenuItemSchemaType[];
}

export const ContextMenuItemSchema: Schema.Codec<ContextMenuItemSchemaType> = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  destructive: Schema.optionalKey(Schema.Boolean),
  disabled: Schema.optionalKey(Schema.Boolean),
  header: Schema.optionalKey(Schema.Boolean),
  icon: Schema.optionalKey(Schema.String),
  children: Schema.optionalKey(
    Schema.Array(
      Schema.suspend((): Schema.Codec<ContextMenuItemSchemaType> => ContextMenuItemSchema),
    ),
  ),
});

export type DesktopUpdateStatus =
  | "disabled"
  | "idle"
  | "checking"
  | "up-to-date"
  | "available"
  | "downloading"
  | "downloaded"
  | "error";

export type DesktopRuntimeArch = "arm64" | "x64" | "other";
export type DesktopTheme = "light" | "dark" | "system";
export type DesktopUpdateChannel = "latest" | "nightly";
export type DesktopAppStageLabel = "Alpha" | "Dev" | "Nightly";

export const DesktopUpdateStatusSchema = Schema.Literals([
  "disabled",
  "idle",
  "checking",
  "up-to-date",
  "available",
  "downloading",
  "downloaded",
  "error",
]);
export const DesktopRuntimeArchSchema = Schema.Literals(["arm64", "x64", "other"]);
export const DesktopThemeSchema = Schema.Literals(["light", "dark", "system"]);
export const DesktopUpdateChannelSchema = Schema.Literals(["latest", "nightly"]);
export const DesktopAppStageLabelSchema = Schema.Literals(["Alpha", "Dev", "Nightly"]);

export interface DesktopAppBranding {
  baseName: string;
  stageLabel: DesktopAppStageLabel;
  displayName: string;
  /**
   * Present only when this Desk is not the person's own Cleo: a development
   * copy run from a checkout (`subject` is its worktree) or a test copy started
   * by the test-identity rail (`subject` is the member it is signed in as).
   * Absent on the installed app and on Desks older than this field.
   */
  copy?: { kind: "development" | "test"; subject: string };
}

export const DesktopAppBrandingSchema = Schema.Struct({
  baseName: Schema.String,
  stageLabel: DesktopAppStageLabelSchema,
  displayName: Schema.String,
  copy: Schema.optional(
    Schema.Struct({ kind: Schema.Literals(["development", "test"]), subject: Schema.String }),
  ),
});

export interface DesktopRuntimeInfo {
  hostArch: DesktopRuntimeArch;
  appArch: DesktopRuntimeArch;
  runningUnderArm64Translation: boolean;
}

export interface DesktopUpdateState {
  enabled: boolean;
  status: DesktopUpdateStatus;
  channel: DesktopUpdateChannel;
  currentVersion: string;
  hostArch: DesktopRuntimeArch;
  appArch: DesktopRuntimeArch;
  runningUnderArm64Translation: boolean;
  availableVersion: string | null;
  downloadedVersion: string | null;
  releaseNotes: ReadonlyArray<DesktopUpdateReleaseNote>;
  downloadPercent: number | null;
  checkedAt: string | null;
  message: string | null;
  errorContext: "check" | "download" | "install" | null;
  canRetry: boolean;
  /**
   * W-f5dee15c: the version of a download a newer release replaced. The
   * download was dropped and `availableVersion` is the release offered instead.
   * Absent from Desks older than this field.
   */
  replacedDownloadVersion?: string | null;
  /**
   * Can 30: Settings → Update automatically. New versions download in the
   * background and install the next time Cleo Code quits or restarts. Absent
   * from Desks older than this field.
   */
  automatic?: boolean;
  /**
   * Can 30: the person pressed Update. The Desk downloads the release if
   * needed, waits until no Cleo turn is running, then installs it and reopens.
   * Cleared when the install starts or fails. Absent from older Desks.
   */
  updateRequested?: boolean;
  /**
   * Can 30: while a requested Update waits, how many conversations still have
   * a turn running; null when it is not waiting on work. Absent from older Desks.
   */
  waitingForTurns?: number | null;
  /**
   * Can 30: the downloaded release was handed to macOS's updater (Squirrel),
   * which installs it when Cleo Code quits or restarts, whatever Update
   * automatically says now. Absent from older Desks.
   */
  installsOnQuit?: boolean;
}

export interface DesktopUpdateReleaseNote {
  version: string;
  items: ReadonlyArray<string>;
}

export const DesktopUpdateReleaseNoteSchema = Schema.Struct({
  version: Schema.String,
  items: Schema.Array(Schema.String),
});

export const DesktopUpdateStateSchema = Schema.Struct({
  enabled: Schema.Boolean,
  status: DesktopUpdateStatusSchema,
  channel: DesktopUpdateChannelSchema,
  currentVersion: Schema.String,
  hostArch: DesktopRuntimeArchSchema,
  appArch: DesktopRuntimeArchSchema,
  runningUnderArm64Translation: Schema.Boolean,
  availableVersion: Schema.NullOr(Schema.String),
  downloadedVersion: Schema.NullOr(Schema.String),
  releaseNotes: Schema.Array(DesktopUpdateReleaseNoteSchema),
  downloadPercent: Schema.NullOr(Schema.Number),
  checkedAt: Schema.NullOr(Schema.String),
  message: Schema.NullOr(Schema.String),
  errorContext: Schema.NullOr(Schema.Literals(["check", "download", "install"])),
  canRetry: Schema.Boolean,
  replacedDownloadVersion: Schema.optional(Schema.NullOr(Schema.String)),
  automatic: Schema.optional(Schema.Boolean),
  updateRequested: Schema.optional(Schema.Boolean),
  waitingForTurns: Schema.optional(Schema.NullOr(Schema.Number)),
  installsOnQuit: Schema.optional(Schema.Boolean),
});

export interface DesktopUpdateActionResult {
  accepted: boolean;
  completed: boolean;
  state: DesktopUpdateState;
}

export const DesktopUpdateActionResultSchema = Schema.Struct({
  accepted: Schema.Boolean,
  completed: Schema.Boolean,
  state: DesktopUpdateStateSchema,
});

export interface DesktopUpdateCheckResult {
  checked: boolean;
  state: DesktopUpdateState;
}

export const DesktopUpdateCheckResultSchema = Schema.Struct({
  checked: Schema.Boolean,
  state: DesktopUpdateStateSchema,
});

// Stable id for the primary backend. Desktop side wraps
// this with a brand inside DesktopBackendManager; web side keeps it as
// a plain string so the env-runtime can compare against it without
// importing brand machinery from the desktop package.
export const PRIMARY_LOCAL_ENVIRONMENT_ID = "primary";

export interface DesktopEnvironmentBootstrap {
  // Stable backend instance id (e.g. "primary"). The
  // web env runtime keys local environments off this so projects
  // routed to a specific backend reopen against the same one.
  id: string;
  label: string;
  httpBaseUrl: string | null;
  wsBaseUrl: string | null;
  bootstrapToken?: string;
}

export const DesktopEnvironmentBootstrapSchema = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  httpBaseUrl: Schema.NullOr(Schema.String),
  wsBaseUrl: Schema.NullOr(Schema.String),
  bootstrapToken: Schema.optionalKey(Schema.String),
});

export const DesktopSshEnvironmentTargetSchema = Schema.Struct({
  alias: Schema.String,
  hostname: Schema.String,
  username: Schema.NullOr(Schema.String),
  port: Schema.NullOr(Schema.Number),
});
export type DesktopSshEnvironmentTarget = typeof DesktopSshEnvironmentTargetSchema.Type;

export type DesktopSshHostSource = "ssh-config" | "known-hosts";
export const DesktopSshHostSourceSchema = Schema.Literals(["ssh-config", "known-hosts"]);

export interface DesktopDiscoveredSshHost extends DesktopSshEnvironmentTarget {
  source: DesktopSshHostSource;
}

export const DesktopDiscoveredSshHostSchema = Schema.Struct({
  alias: Schema.String,
  hostname: Schema.String,
  username: Schema.NullOr(Schema.String),
  port: Schema.NullOr(Schema.Number),
  source: DesktopSshHostSourceSchema,
});

export interface DesktopSshEnvironmentBootstrap {
  target: DesktopSshEnvironmentTarget;
  httpBaseUrl: string;
  wsBaseUrl: string;
  pairingToken: string | null;
  remotePort?: number;
  remoteServerKind?: "external" | "managed";
}

export const DesktopSshEnvironmentBootstrapSchema = Schema.Struct({
  target: DesktopSshEnvironmentTargetSchema,
  httpBaseUrl: Schema.String,
  wsBaseUrl: Schema.String,
  pairingToken: Schema.NullOr(Schema.String),
  remotePort: Schema.optionalKey(Schema.Number),
  remoteServerKind: Schema.optionalKey(Schema.Literals(["external", "managed"])),
});

export interface DesktopSshPasswordPromptRequest {
  requestId: string;
  destination: string;
  username: string | null;
  prompt: string;
  expiresAt: string;
}

export const DesktopSshPasswordPromptCancelledType = "ssh-password-prompt-cancelled" as const;

export const DesktopSshPasswordPromptCancelledResultSchema = Schema.Struct({
  type: Schema.Literal(DesktopSshPasswordPromptCancelledType),
  message: Schema.String,
});

export const DesktopSshEnvironmentEnsureOptionsSchema = Schema.Struct({
  issuePairingToken: Schema.optionalKey(Schema.Boolean),
});

export const DesktopSshEnvironmentEnsureInputSchema = Schema.Struct({
  target: DesktopSshEnvironmentTargetSchema,
  options: Schema.optionalKey(DesktopSshEnvironmentEnsureOptionsSchema),
});

export const DesktopSshEnvironmentEnsureResultSchema = Schema.Union([
  DesktopSshEnvironmentBootstrapSchema,
  DesktopSshPasswordPromptCancelledResultSchema,
]);

export const DesktopSshHttpBaseUrlInputSchema = Schema.Struct({
  httpBaseUrl: Schema.String,
});

export const DesktopSshBearerBootstrapInputSchema = Schema.Struct({
  httpBaseUrl: Schema.String,
  credential: Schema.String,
});

export const DesktopSshPasswordPromptResolutionInputSchema = Schema.Struct({
  requestId: Schema.String,
  password: Schema.NullOr(Schema.String),
});

export const PersistedSavedEnvironmentRecordSchema = Schema.Struct({
  environmentId: EnvironmentId,
  label: Schema.String,
  wsBaseUrl: Schema.String,
  httpBaseUrl: Schema.String,
  createdAt: Schema.String,
  lastConnectedAt: Schema.NullOr(Schema.String),
  desktopSsh: Schema.optionalKey(DesktopSshEnvironmentTargetSchema),
  relayManaged: Schema.optionalKey(
    Schema.Struct({
      relayUrl: Schema.String,
    }),
  ),
});
export type PersistedSavedEnvironmentRecord = typeof PersistedSavedEnvironmentRecordSchema.Type;

export type DesktopServerExposureMode = "local-only" | "network-accessible";

export const DesktopServerExposureModeSchema = Schema.Literals([
  "local-only",
  "network-accessible",
]);

export interface DesktopServerExposureState {
  mode: DesktopServerExposureMode;
  endpointUrl: string | null;
  advertisedHost: string | null;
}

export const DesktopServerExposureStateSchema = Schema.Struct({
  mode: DesktopServerExposureModeSchema,
  endpointUrl: Schema.NullOr(Schema.String),
  advertisedHost: Schema.NullOr(Schema.String),
});

export interface PickFolderOptions {
  initialPath?: string | null;
}

export const PickFolderOptionsSchema = Schema.Struct({
  initialPath: Schema.optionalKey(Schema.NullOr(Schema.String)),
});

/**
 * A file returned by the desktop theme-file picker. Oversized files carry an
 * empty text so the renderer can reject them by size without the main
 * process ever holding their contents.
 */
export interface PickedThemeFile {
  name: string;
  size: number;
  text: string;
}

export const PickedThemeFileSchema = Schema.Struct({
  name: Schema.String,
  size: Schema.Number,
  text: Schema.String,
});

/**
 * UP11 (upstream #9194 reference): the Desk's own local server can be turned
 * off so the app drives paired, Connect or SSH environments only. `enabled`
 * is the persisted preference; `running` says whether this launch started a
 * local backend at all. They differ until the Desk relaunches.
 */
export interface DesktopLocalEnvironmentState {
  enabled: boolean;
  running: boolean;
  restartRequired: boolean;
}

export const DesktopLocalEnvironmentStateSchema = Schema.Struct({
  enabled: Schema.Boolean,
  running: Schema.Boolean,
  restartRequired: Schema.Boolean,
});

/**
 * LVM25: keep Cleo running in the background. `registration` is the macOS
 * launch agent's status as the system reports it; `resident` says whether a
 * quit hides the app instead of ending the process right now.
 */
export const DesktopBackgroundStateSchema = Schema.Struct({
  enabled: Schema.Boolean,
  registration: Schema.Literals([
    "enabled",
    "requires-approval",
    "not-registered",
    "not-found",
    "development",
    "unsupported",
  ]),
  resident: Schema.Boolean,
  launchedInBackground: Schema.Boolean,
  supported: Schema.Boolean,
});
export type DesktopBackgroundState = typeof DesktopBackgroundStateSchema.Type;

/**
 * UP11 (upstream #11289 reference): the three macOS permissions the Desk
 * guides through one shared onboarding panel. Statuses are the prompt-free
 * reads the desktop host can make itself: `notDetermined` means macOS has not
 * been asked, `unknown` means the probe cannot see (Full Disk Access before
 * any protected store exists), `unsupported` means not macOS.
 */
export type DesktopMacPermissionId = "screenRecording" | "accessibility" | "fullDiskAccess";
export const DesktopMacPermissionIdSchema = Schema.Literals([
  "screenRecording",
  "accessibility",
  "fullDiskAccess",
]);
export const DESKTOP_MAC_PERMISSION_IDS: ReadonlyArray<DesktopMacPermissionId> = [
  "screenRecording",
  "accessibility",
  "fullDiskAccess",
];

export type DesktopMacPermissionStatus =
  | "granted"
  | "denied"
  | "notDetermined"
  | "unknown"
  | "unsupported";
export const DesktopMacPermissionStatusSchema = Schema.Literals([
  "granted",
  "denied",
  "notDetermined",
  "unknown",
  "unsupported",
]);

/**
 * Can 76: the drag card beside System Settings. `shown` while the card with
 * Cleo's tile is up for this permission; `moveToApplications` while Cleo runs
 * from a disk image or a translocated path, where a grant would be lost, so no
 * card is offered.
 */
export type DesktopMacPermissionDragTray = "shown" | "moveToApplications";
export const DesktopMacPermissionDragTraySchema = Schema.Literals(["shown", "moveToApplications"]);

export interface DesktopMacPermissionState {
  id: DesktopMacPermissionId;
  status: DesktopMacPermissionStatus;
  /** True when macOS applies a fresh grant only to newly launched processes. */
  changeRequiresRelaunch: boolean;
  /** Can 76: absent when there is no card to speak of, and from older Desks. */
  dragTray?: DesktopMacPermissionDragTray;
}

export const DesktopMacPermissionStateSchema = Schema.Struct({
  id: DesktopMacPermissionIdSchema,
  status: DesktopMacPermissionStatusSchema,
  changeRequiresRelaunch: Schema.Boolean,
  dragTray: Schema.optionalKey(DesktopMacPermissionDragTraySchema),
});

/**
 * Can 76: a panel's ask for a permission, named by the panel that made it.
 * The owner is a token the panel makes when it mounts; the Desk ties the drag
 * card to it, so a panel's close removes only its own card, and an ask whose
 * panel has already closed puts no card up.
 */
export const DesktopMacPermissionAskSchema = Schema.Struct({
  id: DesktopMacPermissionIdSchema,
  owner: Schema.optionalKey(Schema.String),
});

/**
 * UP11 (upstream #8103 reference): window capture into the composer. A
 * global shortcut (or the composer's own button) captures the frontmost
 * window outside Cleo as an image with the app name, window title and app
 * icon, plus the window's accessibility text when the person has granted
 * Accessibility. The metadata and text come from a short-lived native
 * helper; a helper crash or timeout falls back to the image only, and the
 * result says which. Turning the feature on is the only moment permissions
 * are asked for, and the ask is the person's click in the shared panel;
 * startup and capture itself only read.
 */
export const DESKTOP_WINDOW_CAPTURE_DEFAULT_SHORTCUT = "CommandOrControl+Shift+2";
export const DESKTOP_WINDOW_CAPTURE_SHORTCUT_MAX_LENGTH = 64;
export const DESKTOP_WINDOW_CAPTURE_ACCESSIBILITY_MAX_CHARS = 24_000;
/** The permissions the turn-on panel lists; only Screen Recording blocks Continue. */
export const DESKTOP_WINDOW_CAPTURE_REQUIRED_PERMISSIONS: ReadonlyArray<DesktopMacPermissionId> = [
  "screenRecording",
];
export const DESKTOP_WINDOW_CAPTURE_OPTIONAL_PERMISSIONS: ReadonlyArray<DesktopMacPermissionId> = [
  "accessibility",
];

/** An Electron accelerator: modifier tokens joined by `+` ending in one key. */
export const DesktopWindowCaptureShortcutSchema = Schema.String.check(
  Schema.isTrimmed(),
  Schema.isNonEmpty(),
  Schema.isMaxLength(DESKTOP_WINDOW_CAPTURE_SHORTCUT_MAX_LENGTH),
  Schema.isPattern(/^[A-Za-z0-9+\-=[\]\\;',./`~!@#$%^&*()_{}|:"<>?]+$/u),
);

export interface DesktopWindowCaptureState {
  enabled: boolean;
  shortcut: string;
  /** Whether the shortcut is held by this Desk right now (enabled and registered). */
  shortcutRegistered: boolean;
  /** Capture exists on this platform (a window list the Desk can read). */
  supported: boolean;
  /** The native metadata helper is present; without it every capture is image-only. */
  helperAvailable: boolean;
  /** The permissions turn-on guides through the shared panel: required then optional. */
  requiredPermissions: ReadonlyArray<DesktopMacPermissionId>;
  optionalPermissions: ReadonlyArray<DesktopMacPermissionId>;
}
export const DesktopWindowCaptureStateSchema = Schema.Struct({
  enabled: Schema.Boolean,
  shortcut: DesktopWindowCaptureShortcutSchema,
  shortcutRegistered: Schema.Boolean,
  supported: Schema.Boolean,
  helperAvailable: Schema.Boolean,
  requiredPermissions: Schema.Array(DesktopMacPermissionIdSchema),
  optionalPermissions: Schema.Array(DesktopMacPermissionIdSchema),
});

/**
 * Why a shortcut cannot be saved: not a key chord at all, a Cleo keybinding
 * (the global registration would shadow it inside the app), a chord the
 * operating system or its common apps reserve, or one another app already
 * holds (probed live through the OS global-shortcut registry).
 */
export type DesktopWindowCaptureShortcutConflictKind = "invalid" | "cleo" | "system" | "os";
export const DesktopWindowCaptureShortcutConflictKindSchema = Schema.Literals([
  "invalid",
  "cleo",
  "system",
  "os",
]);
export interface DesktopWindowCaptureShortcutConflict {
  kind: DesktopWindowCaptureShortcutConflictKind;
  detail: string;
}
export const DesktopWindowCaptureShortcutConflictSchema = Schema.Struct({
  kind: DesktopWindowCaptureShortcutConflictKindSchema,
  detail: Schema.String,
});
export interface DesktopWindowCaptureShortcutCheck {
  shortcut: string;
  ok: boolean;
  conflicts: ReadonlyArray<DesktopWindowCaptureShortcutConflict>;
}
export const DesktopWindowCaptureShortcutCheckSchema = Schema.Struct({
  shortcut: Schema.String,
  ok: Schema.Boolean,
  conflicts: Schema.Array(DesktopWindowCaptureShortcutConflictSchema),
});
export interface DesktopWindowCaptureShortcutCheckOptions {
  /** The renderer's current keybinding chords (`mod+shift+j` form); the defaults are always checked. */
  reservedChords?: ReadonlyArray<string>;
}
export const DesktopWindowCaptureShortcutCheckOptionsSchema = Schema.Struct({
  reservedChords: Schema.optionalKey(
    Schema.Array(
      Schema.String.check(Schema.isMaxLength(DESKTOP_WINDOW_CAPTURE_SHORTCUT_MAX_LENGTH)),
    ),
  ),
});
/** A shortcut change: the check that decided it and the state afterwards (unchanged when `check.ok` is false). */
export interface DesktopWindowCaptureShortcutUpdate {
  check: DesktopWindowCaptureShortcutCheck;
  state: DesktopWindowCaptureState;
}
export const DesktopWindowCaptureShortcutUpdateSchema = Schema.Struct({
  check: DesktopWindowCaptureShortcutCheckSchema,
  state: DesktopWindowCaptureStateSchema,
});

/**
 * What became of the accessibility extraction: text from the helper, a
 * refusal because Accessibility is not granted, or a fallback to the image
 * only because the helper crashed, timed out, is missing, or does not exist
 * on this platform.
 */
export type DesktopWindowCaptureAccessibilityOutcome =
  | "extracted"
  | "not-granted"
  | "helper-crashed"
  | "helper-timeout"
  | "helper-missing"
  | "unsupported";
export const DesktopWindowCaptureAccessibilityOutcomeSchema = Schema.Literals([
  "extracted",
  "not-granted",
  "helper-crashed",
  "helper-timeout",
  "helper-missing",
  "unsupported",
]);
export interface DesktopWindowCaptureAccessibility {
  outcome: DesktopWindowCaptureAccessibilityOutcome;
  /** The window's visible text, controls and values, present only when `outcome` is `extracted`. */
  text: string | null;
  truncated: boolean;
}
export const DesktopWindowCaptureAccessibilitySchema = Schema.Struct({
  outcome: DesktopWindowCaptureAccessibilityOutcomeSchema,
  text: Schema.NullOr(
    Schema.String.check(Schema.isMaxLength(DESKTOP_WINDOW_CAPTURE_ACCESSIBILITY_MAX_CHARS)),
  ),
  truncated: Schema.Boolean,
});
export type DesktopWindowCaptureTrigger = "shortcut" | "request";
export const DesktopWindowCaptureTriggerSchema = Schema.Literals(["shortcut", "request"]);
export interface DesktopWindowCaptureResult {
  id: string;
  trigger: DesktopWindowCaptureTrigger;
  /** The PNG on disk under the Desk's state directory; the readback path. */
  path: string;
  mimeType: "image/png";
  sizeBytes: number;
  width: number;
  height: number;
  /** The same PNG as a data URL so the composer can attach it without touching the disk. */
  dataUrl: string;
  capturedAt: string;
  appName: string | null;
  bundleId: string | null;
  windowTitle: string | null;
  /** The app's icon as a PNG data URL when the platform offered one. */
  appIconDataUrl: string | null;
  /** How the window was chosen: the helper named it, or the Desk took the frontmost source it could see. */
  identifiedBy: "helper" | "source-order";
  accessibility: DesktopWindowCaptureAccessibility;
}
export const DesktopWindowCaptureResultSchema = Schema.Struct({
  id: Schema.String.check(Schema.isNonEmpty(), Schema.isMaxLength(128)),
  trigger: DesktopWindowCaptureTriggerSchema,
  path: Schema.String.check(Schema.isNonEmpty()),
  mimeType: Schema.Literal("image/png"),
  sizeBytes: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  width: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  height: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  dataUrl: Schema.String.check(Schema.isPattern(/^data:image\/png;base64,/u)),
  capturedAt: Schema.String,
  appName: Schema.NullOr(Schema.String.check(Schema.isMaxLength(255))),
  bundleId: Schema.NullOr(Schema.String.check(Schema.isMaxLength(255))),
  windowTitle: Schema.NullOr(Schema.String.check(Schema.isMaxLength(1024))),
  appIconDataUrl: Schema.NullOr(Schema.String.check(Schema.isPattern(/^data:image\/png;base64,/u))),
  identifiedBy: Schema.Literals(["helper", "source-order"]),
  accessibility: DesktopWindowCaptureAccessibilitySchema,
});

/** The Desk's window-capture door on the preload bridge. */
export interface DesktopWindowCaptureBridge {
  getState: () => Promise<DesktopWindowCaptureState>;
  /**
   * Turn the feature on or off. On registers the shortcut and off releases it;
   * neither prompts for a permission — the turn-on panel in the renderer asks
   * through `requestMacPermission` on the person's click before calling this.
   */
  setEnabled: (enabled: boolean) => Promise<DesktopWindowCaptureState>;
  /** Save a new shortcut after the same check; a failed check changes nothing. */
  setShortcut: (
    shortcut: string,
    options?: DesktopWindowCaptureShortcutCheckOptions,
  ) => Promise<DesktopWindowCaptureShortcutUpdate>;
  /** Capture now, on request (the composer's button); rejects when unsupported. */
  capture: () => Promise<DesktopWindowCaptureResult>;
  /** Captures the shortcut made; the composer attaches them. */
  onCaptured: (listener: (result: DesktopWindowCaptureResult) => void) => () => void;
}

/**
 * UP11 (upstream #7254 reference): browser profiles for the preview browser.
 * Each profile is its own Chromium partition, so cookies and site data never
 * cross profiles; the built-in Incognito profile omits `persist:` and lives
 * in memory only. Ids are folded into partition names, so control characters
 * are refused; names are for people.
 */
export const BROWSER_PROFILE_NAME_MAX_LENGTH = 48;
export const BROWSER_PROFILE_MAX_COUNT = 24;
export const DesktopBrowserProfileIdSchema = Schema.String.check(
  Schema.isTrimmed(),
  Schema.isNonEmpty(),
  Schema.isMaxLength(64),
  Schema.isPattern(/^[^\p{Cc}]+$/u),
);
export const DesktopBrowserProfileNameSchema = Schema.String.check(
  Schema.isTrimmed(),
  Schema.isNonEmpty(),
  Schema.isMaxLength(BROWSER_PROFILE_NAME_MAX_LENGTH),
);
export type DesktopBrowserProfileKind = "persistent" | "incognito";
export const DesktopBrowserProfileKindSchema = Schema.Literals(["persistent", "incognito"]);
export const DEFAULT_BROWSER_PROFILE_ID = "default";
export const INCOGNITO_BROWSER_PROFILE_ID = "incognito";

export interface DesktopBrowserProfile {
  id: string;
  name: string;
  kind: DesktopBrowserProfileKind;
  /** Default and Incognito ship with the Desk: never renamed, never removed. */
  builtIn: boolean;
}
export const DesktopBrowserProfileSchema = Schema.Struct({
  id: DesktopBrowserProfileIdSchema,
  name: DesktopBrowserProfileNameSchema,
  kind: DesktopBrowserProfileKindSchema,
  builtIn: Schema.Boolean,
});

export interface DesktopBrowserProfilesState {
  profiles: readonly DesktopBrowserProfile[];
  /** Profile new tabs open under; never Incognito. */
  defaultProfileId: string;
}
export const DesktopBrowserProfilesStateSchema = Schema.Struct({
  profiles: Schema.Array(DesktopBrowserProfileSchema),
  defaultProfileId: DesktopBrowserProfileIdSchema,
});

export const DesktopBrowserProfileCreateInputSchema = Schema.Struct({
  name: DesktopBrowserProfileNameSchema,
});
export const DesktopBrowserProfileRenameInputSchema = Schema.Struct({
  profileId: DesktopBrowserProfileIdSchema,
  name: DesktopBrowserProfileNameSchema,
});
export const DesktopBrowserProfileIdInputSchema = Schema.Struct({
  profileId: DesktopBrowserProfileIdSchema,
});

/**
 * Renderer-facing snapshot of a desktop preview tab. Mirrors the main-process
 * PreviewTabState shape but uses serialisable primitives only.
 */
export type DesktopPreviewNavStatus =
  | { kind: "Idle" }
  | { kind: "Loading"; url: string; title: string }
  | { kind: "Success"; url: string; title: string }
  | {
      kind: "LoadFailed";
      url: string;
      title: string;
      code: number;
      description: string;
    };

/**
 * Emulated `prefers-color-scheme` for the guest page. "system" clears the
 * override so the page follows the OS appearance.
 */
export type DesktopPreviewColorScheme = "system" | "light" | "dark";

export const DesktopPreviewColorSchemeSchema: Schema.Codec<DesktopPreviewColorScheme> =
  Schema.Literals(["system", "light", "dark"]);

export const FAVICON_DATA_URL_MAX_LENGTH = 8192;
export const FAVICON_CAPTURED_AT_MAX = 8_640_000_000_000_000;

export interface DesktopPreviewFavicon {
  dataUrl: string;
  pageUrl: string;
  capturedAt: number;
}

export const DesktopPreviewFaviconSchema: Schema.Codec<DesktopPreviewFavicon> = Schema.Struct({
  dataUrl: Schema.String.check(
    Schema.isMaxLength(FAVICON_DATA_URL_MAX_LENGTH),
    Schema.isPattern(/^data:image\/png;base64,[a-z0-9+/]+={0,2}$/i),
  ),
  pageUrl: Schema.String.check(Schema.isMaxLength(2_048)),
  capturedAt: Schema.Number.check(
    Schema.isFinite(),
    Schema.isGreaterThanOrEqualTo(0),
    Schema.isLessThanOrEqualTo(FAVICON_CAPTURED_AT_MAX),
  ),
});

export interface DesktopPreviewTabState {
  tabId: string;
  webContentsId: number | null;
  navStatus: DesktopPreviewNavStatus;
  canGoBack: boolean;
  canGoForward: boolean;
  /** Current zoom factor (1.0 = 100%). */
  zoomFactor: number;
  /** Whether this tab is currently mirrored into a desktop picture-in-picture window. */
  pictureInPicture: boolean;
  colorScheme: DesktopPreviewColorScheme;
  controller: "human" | "agent" | "none";
  favicon?: DesktopPreviewFavicon;
  updatedAt: string;
}

export const DesktopPreviewTabIdSchema = Schema.String.check(Schema.isTrimmed()).check(
  Schema.isNonEmpty(),
);

export const DesktopPreviewNavStatusSchema = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("Idle") }),
  Schema.Struct({
    kind: Schema.Literal("Loading"),
    url: Schema.String,
    title: Schema.String,
  }),
  Schema.Struct({
    kind: Schema.Literal("Success"),
    url: Schema.String,
    title: Schema.String,
  }),
  Schema.Struct({
    kind: Schema.Literal("LoadFailed"),
    url: Schema.String,
    title: Schema.String,
    code: Schema.Number,
    description: Schema.String,
  }),
]);

export interface DesktopPreviewPointerEvent {
  tabId: string;
  phase: "move" | "click";
  x: number;
  y: number;
  sequence: number;
  createdAt: string;
}

/**
 * Static config a renderer needs to mount a preview `<webview>`. Returned
 * atomically by `DesktopPreviewBridge.getPreviewConfig()` so the renderer
 * doesn't have to wait on three separate IPC round-trips before the webview
 * can attach.
 */
export interface DesktopPreviewWebviewConfig {
  /** `persist:cleo-preview` (or whatever the desktop chose). */
  partition: string;
  /**
   * Canonical `<webview webpreferences="...">` string. Encodes the security
   * posture (sandboxed but contextIsolation off so the picker preload can
   * read the page's React DevTools hook). Always present.
   */
  webPreferences: string;
  /**
   * Absolute `file://`-style URL to the picker preload bundle. Set to null
   * when the bundle isn't present (older builds, broken install) — the
   * renderer must then disable element-pick affordances.
   */
  preloadUrl: string | null;
}

export const DesktopPreviewWebviewConfigSchema: Schema.Codec<DesktopPreviewWebviewConfig> =
  Schema.Struct({
    partition: Schema.String,
    webPreferences: Schema.String,
    preloadUrl: Schema.NullOr(Schema.String),
  });

export interface DesktopPreviewAnnotationTheme {
  colorScheme: "light" | "dark";
  radius: string;
  background: string;
  foreground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  border: string;
  input: string;
  ring: string;
  fontSans: string;
  fontMono: string;
}

export const DesktopPreviewAnnotationThemeSchema: Schema.Codec<DesktopPreviewAnnotationTheme> =
  Schema.Struct({
    colorScheme: Schema.Literals(["light", "dark"]),
    radius: Schema.String,
    background: Schema.String,
    foreground: Schema.String,
    popover: Schema.String,
    popoverForeground: Schema.String,
    primary: Schema.String,
    primaryForeground: Schema.String,
    muted: Schema.String,
    mutedForeground: Schema.String,
    accent: Schema.String,
    accentForeground: Schema.String,
    border: Schema.String,
    input: Schema.String,
    ring: Schema.String,
    fontSans: Schema.String,
    fontMono: Schema.String,
  });

export interface DesktopPreviewRecordingFrame {
  tabId: string;
  data: string;
  width: number;
  height: number;
  receivedAt: string;
}

export interface DesktopPreviewRecordingArtifact {
  id: string;
  tabId: string;
  path: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export const DesktopPreviewRecordingArtifactSchema: Schema.Codec<DesktopPreviewRecordingArtifact> =
  Schema.Struct({
    id: Schema.String,
    tabId: DesktopPreviewTabIdSchema,
    path: Schema.String,
    mimeType: Schema.String,
    sizeBytes: Schema.Int,
    createdAt: Schema.String,
  });

export interface DesktopPreviewScreenshotArtifact {
  id: string;
  tabId: string;
  path: string;
  mimeType: "image/png";
  sizeBytes: number;
  createdAt: string;
}

export const DesktopPreviewScreenshotArtifactSchema: Schema.Codec<DesktopPreviewScreenshotArtifact> =
  Schema.Struct({
    id: Schema.String,
    tabId: DesktopPreviewTabIdSchema,
    path: Schema.String,
    mimeType: Schema.Literal("image/png"),
    sizeBytes: Schema.Int,
    createdAt: Schema.String,
  });

/**
 * Single stack frame captured by react-grab's `getElementContext`. We surface
 * the source file/line so coding agents can jump straight to the JSX that
 * produced the picked DOM node.
 */
export interface PickedElementStackFrame {
  functionName: string | null;
  fileName: string | null;
  lineNumber: number | null;
  columnNumber: number | null;
}

export const PickedElementStackFrameSchema: Schema.Codec<PickedElementStackFrame> = Schema.Struct({
  functionName: Schema.NullOr(Schema.String),
  fileName: Schema.NullOr(Schema.String),
  lineNumber: Schema.NullOr(Schema.Number),
  columnNumber: Schema.NullOr(Schema.Number),
});

/**
 * A successful element pick from the preview webview. All fields are
 * best-effort — pages that don't ship a React fiber tree (or aren't running
 * in dev) will still produce a usable payload (selector + html preview),
 * just without component / source attribution.
 */
export interface PickedElementPayload {
  /** URL of the page the element was picked on. */
  pageUrl: string;
  /** Optional `<title>` of that page (best-effort). */
  pageTitle: string | null;
  /** Lowercase tag name, e.g. `"button"`. */
  tagName: string;
  /** CSS selector resolving back to the element on a re-render. */
  selector: string | null;
  /** Truncated outer-HTML preview (matches react-grab's `htmlPreview`). */
  htmlPreview: string;
  /** Nearest React component display name, or null when unavailable. */
  componentName: string | null;
  /** First source-mapped stack frame (file + line of the JSX source). */
  source: PickedElementStackFrame | null;
  /** Full owner-stack frames; can be empty. Useful for richer context. */
  stack: ReadonlyArray<PickedElementStackFrame>;
  /** Author CSS only (UA defaults stripped) — react-grab's `styles`. */
  styles: string;
  /** Wall-clock pick time as ISO-8601 string. */
  pickedAt: string;
}

export const PickedElementPayloadSchema: Schema.Codec<PickedElementPayload> = Schema.Struct({
  pageUrl: Schema.String,
  pageTitle: Schema.NullOr(Schema.String),
  tagName: Schema.String,
  selector: Schema.NullOr(Schema.String),
  htmlPreview: Schema.String,
  componentName: Schema.NullOr(Schema.String),
  source: Schema.NullOr(PickedElementStackFrameSchema),
  stack: Schema.Array(PickedElementStackFrameSchema),
  styles: Schema.String,
  pickedAt: Schema.String,
});

export interface PreviewAnnotationRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const PreviewAnnotationRectSchema: Schema.Codec<PreviewAnnotationRect> = Schema.Struct({
  x: Schema.Number,
  y: Schema.Number,
  width: Schema.Number,
  height: Schema.Number,
});

export interface PreviewAnnotationPoint {
  x: number;
  y: number;
}

export const PreviewAnnotationPointSchema: Schema.Codec<PreviewAnnotationPoint> = Schema.Struct({
  x: Schema.Number,
  y: Schema.Number,
});

export interface PreviewAnnotationElementTarget {
  id: string;
  element: PickedElementPayload;
  rect: PreviewAnnotationRect;
}

export const PreviewAnnotationElementTargetSchema: Schema.Codec<PreviewAnnotationElementTarget> =
  Schema.Struct({
    id: Schema.String,
    element: PickedElementPayloadSchema,
    rect: PreviewAnnotationRectSchema,
  });

export interface PreviewAnnotationRegionTarget {
  id: string;
  rect: PreviewAnnotationRect;
}

export const PreviewAnnotationRegionTargetSchema: Schema.Codec<PreviewAnnotationRegionTarget> =
  Schema.Struct({
    id: Schema.String,
    rect: PreviewAnnotationRectSchema,
  });

export interface PreviewAnnotationStrokeTarget {
  id: string;
  color: string;
  width: number;
  points: ReadonlyArray<PreviewAnnotationPoint>;
  bounds: PreviewAnnotationRect;
}

export const PreviewAnnotationStrokeTargetSchema: Schema.Codec<PreviewAnnotationStrokeTarget> =
  Schema.Struct({
    id: Schema.String,
    color: Schema.String,
    width: Schema.Number,
    points: Schema.Array(PreviewAnnotationPointSchema),
    bounds: PreviewAnnotationRectSchema,
  });

export interface PreviewAnnotationStyleChange {
  targetId: string;
  selector: string | null;
  property: string;
  previousValue: string;
  value: string;
}

export const PreviewAnnotationStyleChangeSchema: Schema.Codec<PreviewAnnotationStyleChange> =
  Schema.Struct({
    targetId: Schema.String,
    selector: Schema.NullOr(Schema.String),
    property: Schema.String,
    previousValue: Schema.String,
    value: Schema.String,
  });

/** CCD17: what one Canvas pick hit in a Component frame, before attribution. */
export interface DesktopCanvasPickHit {
  readonly frameUrl: string;
  readonly text: string;
  readonly tagName: string;
  readonly rect: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  } | null;
}

/**
 * CCD17: an annotation of an open Canvas board names the exact revision and
 * placement it was taken on, and the one input whose saved value rendered the
 * picked text, or null with its candidates when that cannot be told apart.
 * On a page Canvas (Can 17) there is no placement: nodeId is the page's file,
 * and page names that file and the clicked element's tag and visible text.
 */
export interface PreviewAnnotationCanvasTarget {
  readonly canvasId: string;
  readonly sequence: number;
  readonly nodeId: string;
  readonly path: string | null;
  readonly value: string | null;
  readonly candidates: ReadonlyArray<string>;
  readonly page?: { readonly file: string; readonly tagName: string; readonly text: string };
}
export const PreviewAnnotationCanvasTargetSchema: Schema.Codec<PreviewAnnotationCanvasTarget> =
  Schema.Struct({
    canvasId: Schema.String,
    sequence: Schema.Number,
    nodeId: Schema.String,
    path: Schema.NullOr(Schema.String),
    value: Schema.NullOr(Schema.String),
    candidates: Schema.Array(Schema.String),
    page: Schema.optionalKey(
      Schema.Struct({ file: Schema.String, tagName: Schema.String, text: Schema.String }),
    ),
  });

export interface PreviewAnnotationScreenshot {
  dataUrl: string;
  width: number;
  height: number;
  cropRect: PreviewAnnotationRect;
}

export const PreviewAnnotationScreenshotSchema: Schema.Codec<PreviewAnnotationScreenshot> =
  Schema.Struct({
    dataUrl: Schema.String,
    width: Schema.Number,
    height: Schema.Number,
    cropRect: PreviewAnnotationRectSchema,
  });

/**
 * A submitted preview annotation. One annotation may reference multiple DOM
 * elements, freeform regions, and ink strokes. The desktop main process adds
 * the screenshot after the guest preload submits the structured draft.
 */
export interface PreviewAnnotationPayload {
  id: string;
  pageUrl: string;
  pageTitle: string | null;
  comment: string;
  elements: ReadonlyArray<PreviewAnnotationElementTarget>;
  regions: ReadonlyArray<PreviewAnnotationRegionTarget>;
  strokes: ReadonlyArray<PreviewAnnotationStrokeTarget>;
  styleChanges: ReadonlyArray<PreviewAnnotationStyleChange>;
  screenshot: PreviewAnnotationScreenshot | null;
  createdAt: string;
  /** Present when the annotated page is an open Canvas board (CCD17). */
  canvas?: PreviewAnnotationCanvasTarget;
}

export const PreviewAnnotationPayloadSchema: Schema.Codec<PreviewAnnotationPayload> = Schema.Struct(
  {
    id: Schema.String,
    pageUrl: Schema.String,
    pageTitle: Schema.NullOr(Schema.String),
    comment: Schema.String,
    elements: Schema.Array(PreviewAnnotationElementTargetSchema),
    regions: Schema.Array(PreviewAnnotationRegionTargetSchema),
    strokes: Schema.Array(PreviewAnnotationStrokeTargetSchema),
    styleChanges: Schema.Array(PreviewAnnotationStyleChangeSchema),
    screenshot: Schema.NullOr(PreviewAnnotationScreenshotSchema),
    createdAt: Schema.String,
    canvas: Schema.optionalKey(PreviewAnnotationCanvasTargetSchema),
  },
);

export type PreviewAnnotationSubmission = "attach" | "send";
export const PreviewAnnotationSubmissionSchema: Schema.Codec<PreviewAnnotationSubmission> =
  Schema.Literals(["attach", "send"]);

export interface PreviewAnnotationSubmissionResult {
  annotation: PreviewAnnotationPayload;
  submission: PreviewAnnotationSubmission;
}
export const PreviewAnnotationSubmissionResultSchema: Schema.Codec<PreviewAnnotationSubmissionResult> =
  Schema.Struct({
    annotation: PreviewAnnotationPayloadSchema,
    submission: PreviewAnnotationSubmissionSchema,
  });

export const DesktopPreviewTabInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
});

export const DesktopPreviewRegisterWebviewInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
  webContentsId: Schema.Int.check(Schema.isGreaterThan(0)),
});

/**
 * Who asked for a preview navigation. "person" is the person's own address-bar navigation in the Desk;
 * "automation" is the agent-driven preview tools. Absent means "automation": an unmarked call is never
 * mistaken for the person's gesture (W-09ce60ee, the navigation restriction can only be released by a
 * person's own navigation that succeeds).
 */
export const DesktopPreviewNavigationInitiatorSchema = Schema.Literals(["person", "automation"]);
export type DesktopPreviewNavigationInitiator = typeof DesktopPreviewNavigationInitiatorSchema.Type;

export const DesktopPreviewNavigateInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
  url: Schema.String,
  initiator: Schema.optional(DesktopPreviewNavigationInitiatorSchema),
});

export const DesktopPreviewConfigInputSchema = Schema.Struct({
  environmentId: EnvironmentId,
  // UP11: the profile whose partition the webview mounts under. Omitted means
  // the built-in Default profile — the scope the browser used before
  // profiles; the renderer resolves the configured default when it opens a
  // tab and names it from then on. The partition string is derived in main
  // only: will-attach-webview prefix-checks, so a renderer-supplied partition
  // could otherwise attach to a session without the UA rewrite and the
  // permission handlers.
  profileId: Schema.optional(DesktopBrowserProfileIdSchema),
});
export const DesktopPreviewClearDataInputSchema = Schema.Struct({
  environmentId: Schema.optional(EnvironmentId),
  /** Omit to clear every partition; otherwise only this profile's. */
  profileId: Schema.optional(DesktopBrowserProfileIdSchema),
});

export const DesktopPreviewSetColorSchemeInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
  colorScheme: DesktopPreviewColorSchemeSchema,
});

export const DesktopPreviewAnnotationThemeInputSchema = Schema.Struct({
  theme: DesktopPreviewAnnotationThemeSchema,
});

export const DesktopPreviewArtifactInputSchema = Schema.Struct({
  path: Schema.String.check(Schema.isTrimmed()).check(Schema.isNonEmpty()),
});

export const DesktopPreviewRecordingSaveInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
  mimeType: Schema.String.check(Schema.isTrimmed()).check(Schema.isNonEmpty()),
  data: Schema.Uint8Array,
});

export const DesktopPreviewAutomationClickInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
  input: PreviewAutomationClickInput,
});

export const DesktopPreviewAutomationTypeInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
  input: PreviewAutomationTypeInput,
});

export const DesktopPreviewAutomationPressInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
  input: PreviewAutomationPressInput,
});

export const DesktopPreviewAutomationScrollInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
  input: PreviewAutomationScrollInput,
});

export const DesktopPreviewAutomationEvaluateInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
  input: PreviewAutomationEvaluateInput,
});

export const DesktopPreviewAutomationWaitForInputSchema = Schema.Struct({
  tabId: DesktopPreviewTabIdSchema,
  input: PreviewAutomationWaitForInput,
});

export interface DesktopBridge {
  localComputer?: (
    command: import("./localComputer.ts").LocalComputerCommand,
  ) => Promise<import("./localComputer.ts").LocalComputerState>;
  connectClaudeAccount?: (
    request?: import("./claudeAccounts.ts").ClaudeAccountOperation,
  ) => Promise<import("./claudeAccounts.ts").ClaudeConnectionState>;
  getClaudeAccountConnection?: () => Promise<import("./claudeAccounts.ts").ClaudeConnectionState>;
  cancelClaudeAccountConnection?: () => Promise<
    import("./claudeAccounts.ts").ClaudeConnectionState
  >;
  getAppBranding: () => DesktopAppBranding | null;
  // One bootstrap per pool instance currently registered with bootstrap
  // info (omits instances whose backend hasn't produced a config yet).
  // The primary backend is identified by id === PRIMARY_LOCAL_ENVIRONMENT_ID.
  getLocalEnvironmentBootstraps: () => readonly DesktopEnvironmentBootstrap[];
  getLocalEnvironmentBearerToken: () => Promise<string>;
  /** CCS1: a PNG of the Desk's window as it is now; null when no window can be captured. Absent on Desks older than this method. */
  captureDeskView?: () => Promise<{ base64: string; width: number; height: number } | null>;
  /**
   * CVA4: a PNG of one Canvas build's composition, rendered in an isolated
   * hidden window of this Desk at the given viewport and theme once the
   * viewer reports readiness. Absent on Desks older than this method.
   */
  /**
   * CCD17: one pick over the open Canvas board's own Component frames; the
   * first click answers with that element's visible text and box. Absent on
   * Desks older than this method.
   */
  canvasPick?: {
    pickElement: (frameUrls: ReadonlyArray<string>) => Promise<DesktopCanvasPickHit | null>;
    cancel: () => Promise<void>;
  };
  captureCanvasThumbnail?: (input: {
    url: string;
    width: number;
    height: number;
    theme: string;
    timeoutMs?: number;
  }) => Promise<
    { ok: true; base64: string; width: number; height: number } | { ok: false; reason: string }
  >;
  getClientSettings: () => Promise<ClientSettings | null>;
  setClientSettings: (settings: ClientSettings) => Promise<void>;
  /**
   * W-8ecf392a: write only these fields over what the file holds now, so a
   * window's stale snapshot never undoes another process's change (the
   * server's settings door). Optional: older preloads lack it.
   */
  patchClientSettings?: (patch: ClientSettingsPatch) => Promise<void>;
  /**
   * Register §41 (D-US2): fires with the freshly decoded settings whenever
   * client-settings.json is atomically replaced on disk by ANY writer — the
   * renderer's own set, or the server's gated settings door — so a change
   * Cleo makes is felt without a restart. Optional: older preloads lack it.
   */
  onClientSettingsChange?: (listener: (settings: ClientSettings) => void) => () => void;
  getConnectionCatalog?: () => Promise<string | null>;
  setConnectionCatalog?: (catalog: string) => Promise<boolean>;
  /** Keeps a copy of an unreadable catalog in the Desk's one quarantine slot. */
  quarantineConnectionCatalog?: (catalog: string) => Promise<boolean>;
  discoverSshHosts: () => Promise<readonly DesktopDiscoveredSshHost[]>;
  ensureSshEnvironment: (
    target: DesktopSshEnvironmentTarget,
    options?: { issuePairingToken?: boolean },
  ) => Promise<DesktopSshEnvironmentBootstrap>;
  disconnectSshEnvironment: (target: DesktopSshEnvironmentTarget) => Promise<void>;
  fetchSshEnvironmentDescriptor: (httpBaseUrl: string) => Promise<ExecutionEnvironmentDescriptor>;
  bootstrapSshBearerSession: (
    httpBaseUrl: string,
    credential: string,
  ) => Promise<AuthAccessTokenResult>;
  onSshPasswordPrompt: (listener: (request: DesktopSshPasswordPromptRequest) => void) => () => void;
  resolveSshPasswordPrompt: (requestId: string, password: string | null) => Promise<void>;
  getServerExposureState: () => Promise<DesktopServerExposureState>;
  setServerExposureMode: (mode: DesktopServerExposureMode) => Promise<DesktopServerExposureState>;
  getAdvertisedEndpoints: () => Promise<readonly AdvertisedEndpoint[]>;
  /**
   * UP11: whether this Desk runs its own local server. The getter is
   * synchronous so the renderer can decide at boot, before any atom runs,
   * whether a primary environment exists at all. The setter persists the
   * preference and relaunches the Desk when it actually changed, because the
   * bootstrap decides once per launch. Optional: older desktop builds lack
   * both, and are treated as enabled.
   */
  getLocalEnvironmentState?: () => DesktopLocalEnvironmentState;
  setLocalEnvironmentEnabled?: (enabled: boolean) => Promise<DesktopLocalEnvironmentState>;
  /** LVM25: keep Cleo running in the background. Optional: older Desks lack it. */
  getBackgroundState?: () => Promise<DesktopBackgroundState>;
  setBackgroundEnabled?: (enabled: boolean) => Promise<DesktopBackgroundState>;
  /**
   * UP11: prompt-free status of the three onboarding permissions on this Mac,
   * read by the desktop host itself so the panel works with the local
   * environment off. Off macOS every entry is `unsupported`. Optional: older
   * desktop builds lack it.
   */
  getMacPermissionStatuses?: () => Promise<readonly DesktopMacPermissionState[]>;
  /**
   * UP11: the panel's Allow. Accessibility and Screen Recording run macOS's
   * prompt-and-register call; Full Disk Access has no prompt API. Whenever the
   * permission is still not granted afterwards, Allow opens the exact System
   * Settings pane with the drag card beside it (Can 76). Resolves to the
   * status afterwards.
   */
  requestMacPermission?: (
    id: DesktopMacPermissionId,
    owner?: string,
  ) => Promise<DesktopMacPermissionState>;
  /**
   * Can 76: opens the exact System Settings pane with the drag card beside
   * it, without macOS's own prompt. Resolves to the status, with `dragTray`
   * saying whether the card is up. Optional: older desktop builds lack it.
   * `owner` names the asking panel (see DesktopMacPermissionAskSchema).
   */
  showMacPermissionSettings?: (
    id: DesktopMacPermissionId,
    owner?: string,
  ) => Promise<DesktopMacPermissionState>;
  /**
   * Can 76: closes the drag card when the panel that asked for it closes:
   * only that owner's card, and that owner's asks still in flight put none
   * up. Without an owner, closes whatever card is up.
   */
  closeMacPermissionTray?: (owner?: string) => Promise<void>;
  /**
   * UP11 (upstream #8103 reference): window capture into the composer.
   * Optional: older desktop builds lack it, and a browser never has it.
   */
  windowCapture?: DesktopWindowCaptureBridge;
  /**
   * UP6: the dock/taskbar badge count for unread session notices; the Desk
   * clears it when a window gains focus. Optional: older desktop builds lack it.
   */
  setNotificationBadge?: (badge: { readonly count: number }) => Promise<void>;
  pickFolder: (options?: PickFolderOptions) => Promise<string | null>;
  /**
   * macOS's prompt-and-register call for Accessibility: shows the system
   * dialog and lists this app in the Accessibility pane (toggle off)
   * without performing any accessibility action. Resolves to whether the
   * app is trusted right now; false on other platforms. Optional: older
   * desktop builds lack it.
   */
  requestAccessibility?: () => Promise<boolean>;
  /**
   * macOS's prompt-and-register trigger for Screen Recording: makes TCC
   * show the system dialog and list this app in the Screen & System Audio
   * Recording pane (toggle off) without keeping any capture. Resolves to
   * whether capture is granted right now — a fresh grant still needs an app
   * relaunch; false on other platforms. Optional: older desktop builds
   * lack it.
   */
  requestScreenRecording?: () => Promise<boolean>;
  /**
   * Multi-select JSON file picker that opens in the VS Code extensions
   * directory when one exists. Optional: older desktop builds lack it, and
   * web callers fall back to a plain file input.
   */
  pickThemeFiles?: () => Promise<readonly PickedThemeFile[] | null>;
  /** Select a GGUF path on the desktop host; no model bytes cross the renderer bridge. */
  pickLocalModelFile?: () => Promise<string | null>;
  setTheme: (theme: DesktopTheme) => Promise<void>;
  showContextMenu: <T extends string>(
    items: readonly ContextMenuItem<T>[],
    position?: { x: number; y: number },
  ) => Promise<T | null>;
  openExternal: (url: string) => Promise<boolean>;
  /**
   * Bring the Desk window to the front (restore, show, focus). The Neucleos
   * sign-in finishes in the system browser; this is how the renderer hands
   * the person back to the Desk once the deposit lands. Optional: older
   * desktop builds lack it.
   */
  focusWindow?: () => Promise<void>;
  /**
   * Probe this desktop machine for installed remote-capable editor CLIs
   * (used for remote open-in-editor deep links). Optional: older desktop
   * builds lack it; callers fall back to VS Code only.
   */
  probeRemoteEditors?: () => Promise<readonly EditorId[]>;
  onMenuAction: (listener: (action: string) => void) => () => void;
  getWindowFullscreenState: () => boolean;
  onWindowFullscreenStateChange: (listener: (fullscreen: boolean) => void) => () => void;
  getUpdateState: () => Promise<DesktopUpdateState>;
  setUpdateChannel: (channel: DesktopUpdateChannel) => Promise<DesktopUpdateState>;
  checkForUpdate: () => Promise<DesktopUpdateCheckResult>;
  downloadUpdate: () => Promise<DesktopUpdateActionResult>;
  installUpdate: () => Promise<DesktopUpdateActionResult>;
  /**
   * Can 30: one click to update. Downloads the release if needed, waits for
   * Cleo's running turns, then installs and reopens. Optional: older Desks
   * lack it (callers fall back to download, then install).
   */
  updateNow?: () => Promise<DesktopUpdateActionResult>;
  /** Can 30: Settings → Update automatically. Optional: older Desks lack it. */
  setUpdateAutomatically?: (enabled: boolean) => Promise<DesktopUpdateState>;
  onUpdateState: (listener: (state: DesktopUpdateState) => void) => () => void;
  /**
   * Installing an update destroys the window, which skips beforeunload. The
   * Desk calls `save` first and installs only when it resolves; a rejection
   * holds the install back. A successful save leaves the drafts sealed (no
   * change accepted) until the window is destroyed or `release` is called
   * because the install did not go ahead. Returns an unsubscribe.
   */
  onComposerDraftFlushRequest?: (save: () => Promise<void>, release: () => void) => () => void;
  /**
   * Desktop-only preview surface. Present iff the renderer is hosted by the
   * Electron desktop build; web builds have `preview === undefined`.
   */
  preview?: DesktopPreviewBridge;
}

export interface DesktopPreviewBridge {
  createTab: (tabId: string) => Promise<void>;
  closeTab: (tabId: string) => Promise<void>;
  registerWebview: (tabId: string, webContentsId: number) => Promise<void>;
  navigate: (
    tabId: string,
    url: string,
    initiator?: DesktopPreviewNavigationInitiator,
  ) => Promise<void>;
  goBack: (tabId: string) => Promise<void>;
  goForward: (tabId: string) => Promise<void>;
  refresh: (tabId: string) => Promise<void>;
  zoomIn: (tabId: string) => Promise<void>;
  zoomOut: (tabId: string) => Promise<void>;
  resetZoom: (tabId: string) => Promise<void>;
  /** Reload bypassing the HTTP cache. */
  hardReload: (tabId: string) => Promise<void>;
  /**
   * Emulate `prefers-color-scheme` on the guest page ("system" clears the
   * override). Persists per tab and is re-applied across webview swaps.
   */
  setColorScheme: (tabId: string, colorScheme: DesktopPreviewColorScheme) => Promise<void>;
  /** Open the guest webview's DevTools (detached). */
  openDevTools: (tabId: string) => Promise<void>;
  /**
   * Drop cookies + storage data for the preview partitions. Without
   * arguments every partition is cleared (the historical all-profiles
   * action); with an environment and profile only that one partition.
   */
  clearCookies: (environmentId?: EnvironmentId, profileId?: string) => Promise<void>;
  /** Drop the HTTP cache for the preview partitions (same scoping). */
  clearCache: (environmentId?: EnvironmentId, profileId?: string) => Promise<void>;
  /**
   * One-shot config for mounting a preview `<webview>`. Replaces three
   * earlier round-trip calls (`getBrowserPartition`, `getWebviewPreferences`,
   * `getPickPreloadPath`) so adding a new field here only requires touching
   * the contract + main, not the renderer's mount logic. `profileId` (UP11)
   * picks the browser profile; omitted means the built-in Default.
   */
  getPreviewConfig: (
    environmentId: EnvironmentId,
    profileId?: string,
  ) => Promise<DesktopPreviewWebviewConfig>;
  /**
   * UP11: browser profiles. Create, rename, set default and remove; removal
   * clears the profile's cookies, storage and cache in every environment it
   * was used in first, and keeps the profile when that clearing fails.
   * Optional: older desktop builds lack it.
   */
  browserProfiles?: {
    list: () => Promise<DesktopBrowserProfilesState>;
    create: (name: string) => Promise<DesktopBrowserProfilesState>;
    rename: (profileId: string, name: string) => Promise<DesktopBrowserProfilesState>;
    setDefault: (profileId: string) => Promise<DesktopBrowserProfilesState>;
    remove: (profileId: string) => Promise<DesktopBrowserProfilesState>;
  };
  setAnnotationTheme: (theme: DesktopPreviewAnnotationTheme) => Promise<void>;
  /**
   * Activate the in-page element picker for the given tab. Resolves with
   * the picked annotation and its attach/send intent, or `null` when the
   * user cancels (Escape / nav). The promise rejects if the picker can't be
   * activated (no webview, etc.).
   */
  pickElement: (tabId: string) => Promise<PreviewAnnotationSubmissionResult | null>;
  /** Cancel an in-flight preview annotation session. */
  cancelPickElement: (tabId: string) => Promise<void>;
  captureScreenshot: (tabId: string) => Promise<DesktopPreviewScreenshotArtifact>;
  revealArtifact: (path: string) => Promise<void>;
  copyArtifactToClipboard: (path: string) => Promise<void>;
  pictureInPicture: {
    open: (tabId: string) => Promise<void>;
    close: (tabId: string) => Promise<void>;
  };
  recording: {
    startScreencast: (tabId: string) => Promise<void>;
    stopScreencast: (tabId: string) => Promise<void>;
    save: (
      tabId: string,
      mimeType: string,
      data: Uint8Array,
    ) => Promise<DesktopPreviewRecordingArtifact>;
    onFrame: (listener: (frame: DesktopPreviewRecordingFrame) => void) => () => void;
  };
  automation: {
    status: (tabId: string) => Promise<PreviewAutomationStatus>;
    snapshot: (tabId: string) => Promise<PreviewAutomationSnapshot>;
    click: (tabId: string, input: PreviewAutomationClickInput) => Promise<void>;
    type: (tabId: string, input: PreviewAutomationTypeInput) => Promise<void>;
    press: (tabId: string, input: PreviewAutomationPressInput) => Promise<void>;
    scroll: (tabId: string, input: PreviewAutomationScrollInput) => Promise<void>;
    evaluate: (tabId: string, input: PreviewAutomationEvaluateInput) => Promise<unknown>;
    waitFor: (tabId: string, input: PreviewAutomationWaitForInput) => Promise<void>;
  };
  onStateChange: (listener: (tabId: string, state: DesktopPreviewTabState) => void) => () => void;
  onPointerEvent: (listener: (event: DesktopPreviewPointerEvent) => void) => () => void;
}

export type ConfirmDialogVariant = "default" | "destructive";

export interface ConfirmDialogOptions {
  readonly variant?: ConfirmDialogVariant;
}

/**
 * APIs bound to the local app shell, not to any particular backend environment.
 *
 * These capabilities describe the desktop/browser host that the user is
 * currently running: dialogs, external-link opening, context menus, and
 * app-level settings/config access. They must not be used as a proxy for
 * "whatever environment the user is targeting", because in a multi-environment
 * world the local shell and a selected backend environment are distinct
 * concepts.
 */
export interface LocalApi {
  dialogs: {
    pickFolder: (options?: PickFolderOptions) => Promise<string | null>;
    confirm: (message: string, options?: ConfirmDialogOptions) => Promise<boolean>;
  };
  permissions: {
    /**
     * Ask macOS to show its Accessibility prompt and register the app in
     * the Accessibility pane. Null when this host cannot ask — a plain
     * browser, or an older desktop build without the bridge method — so
     * callers gate their affordance on presence, not on a false result.
     */
    requestAccessibility: (() => Promise<boolean>) | null;
    /**
     * Ask macOS to show its Screen Recording prompt and register the app in
     * the Screen & System Audio Recording pane. Null when this host cannot
     * ask — a plain browser, or an older desktop build without the bridge
     * method — so callers gate their affordance on presence, not on a false
     * result.
     */
    requestScreenRecording: (() => Promise<boolean>) | null;
  };
  shell: {
    openExternal: (url: string) => Promise<void>;
    /** Bring this app to the front: the desktop window on the Desk, `window.focus()` in a browser. */
    focusWindow: () => Promise<void>;
  };
  contextMenu: {
    show: <T extends string>(
      items: readonly ContextMenuItem<T>[],
      position?: { x: number; y: number },
    ) => Promise<T | null>;
    close: () => Promise<void>;
  };
  persistence: {
    getClientSettings: () => Promise<ClientSettings | null>;
    setClientSettings: (settings: ClientSettings) => Promise<void>;
    /**
     * W-8ecf392a: the fields a window changed, written over what is persisted
     * now. The window never sends its whole snapshot: one taken before
     * another process changed a field would put the old value back.
     */
    patchClientSettings: (patch: ClientSettingsPatch) => Promise<void>;
    /** D-US2: external replacements of the persisted client settings; absent in the browser. */
    onClientSettingsChange?: (listener: (settings: ClientSettings) => void) => () => void;
  };
}
